import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shipmentsAPI } from '../services/api';
import RejectionModal from './RejectionModal';
import RejectionBanner from './RejectionBanner';
import AttachmentSection from './AttachmentSection';
import { formatDateTimeEST, formatDateEST } from '../utils/dateFormat';
import {
  flagYesNo,
  isYesFlag,
  formatLineItemWeight,
  formatLineItemDimensions,
  formatBillTo,
  formatService,
} from '../utils/shipmentDisplay';
import './ShipmentDetailModal.css';

function ShipmentDetailModal({ shipment, onClose, onClone, showCloneButton = true, showDeleteButton = false, onDelete, onReject }) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  if (!shipment) return null;

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const isOwner = shipment.user_id === currentUser?.id;
  const canReject = isAdmin && (shipment.status === 'submitted' || shipment.status === 'resubmitted');
  const canApprove = isAdmin && (shipment.status === 'submitted' || shipment.status === 'resubmitted');
  const canEditResubmit = shipment.status === 'rejected' && isOwner;

  const formatDate = (dateString) => formatDateEST(dateString);

  const formatDateTime = (dateString) => formatDateTimeEST(dateString);

  const DataRow = ({ label, value, highlight = false }) => {
    if (!value || value === '-' || value === 'N/A') return null;
    return (
      <div className={`detail-row ${highlight ? 'highlight' : ''}`}>
        <span className="detail-label">{label}:</span>
        <span className="detail-value">{value}</span>
      </div>
    );
  };

  const SectionHeader = ({ title }) => (
    <div className="section-header">{title}</div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="shipment-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Shipment Details</h2>
            <span className="shipment-id-badge">#{shipment.id}</span>
            <span className={`shipment-type-badge ${shipment.shipment_type}`}>
              {shipment.shipment_type === 'international' ? 'International' : 'Domestic'}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {/* Rejection Banner */}
          {shipment.status === 'rejected' && shipment.rejection_reason && (
            <RejectionBanner
              rejectionReason={shipment.rejection_reason}
              rejectedByName={shipment.rejected_by_name}
              rejectedAt={shipment.rejected_at}
            />
          )}

          {/* Approval Banner */}
          {shipment.status === 'approved' && shipment.approved_by_name && (
            <div className="rejection-banner" style={{ backgroundColor: '#d4edda', borderLeftColor: '#28a745' }} role="status">
              <div className="rejection-banner-header">
                <span className="rejection-banner-icon">✅</span>
                <strong className="rejection-banner-title" style={{ color: '#155724' }}>Shipment Approved</strong>
              </div>
              <div className="rejection-banner-content">
                <div className="rejection-banner-meta" style={{ color: '#155724' }}>
                  <span>Approved by: <strong>{shipment.approved_by_name}</strong></span>
                  {shipment.approved_at && (
                    <span>{new Date(shipment.approved_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Shipment Information */}
          <SectionHeader title="SHIPMENT INFORMATION" />
          <div className="detail-section">
            <DataRow label="Created Date" value={formatDateTime(shipment.created_at)} />
            <DataRow label="Service" value={formatService(shipment.service)} highlight />
            <DataRow label="Bill To" value={formatBillTo(shipment.bill_to)} />
            <DataRow label="Status" value={shipment.status || 'Pending'} />
            {!!shipment.active_outage && (
              <DataRow label="Active Outage" value="Yes" highlight />
            )}
            {shipment.active_outage !== null && shipment.active_outage !== undefined && !shipment.active_outage && (
              <DataRow label="Active Outage" value="No" />
            )}
          </div>

          {/* Shipper Information */}
          <SectionHeader title="SHIPPER" />
          <div className="detail-section">
            <DataRow label="Company Name" value={shipment.shipper_name} highlight />
            <DataRow label="Address" value={shipment.shipper_address} />
            <DataRow label="Address 2" value={shipment.shipper_address2} />
            <DataRow label="City" value={shipment.shipper_city} />
            <DataRow label="State" value={shipment.shipper_state} />
            <DataRow label="ZIP Code" value={shipment.shipper_zip} />
            <DataRow label="Country" value={shipment.shipper_country} />
            <DataRow label="Contact Person" value={shipment.shipper_contact} />
            <DataRow label="Phone" value={shipment.shipper_phone} />
            <DataRow label="Email" value={shipment.shipper_email} />
            <DataRow label="Charge Code" value={shipment.charge_code} highlight />
            <DataRow label="Reference Number" value={shipment.shipper_reference_num} />
            <DataRow label="ADP Number" value={shipment.adp_num} />
            <DataRow label="NCR Number" value={shipment.ncr_num} />
            <DataRow label="Purchase Order" value={shipment.purchase_order} highlight />
            <DataRow label="Production Order" value={shipment.production_order} highlight />
          </div>

          {/* Consignee Information */}
          <SectionHeader title="CONSIGNEE" />
          <div className="detail-section">
            <DataRow label="Company Name" value={shipment.consignee_name} highlight />
            <DataRow label="Address" value={shipment.consignee_address} />
            <DataRow label="Address 2" value={shipment.consignee_address2} />
            <DataRow label="City" value={shipment.consignee_city} />
            <DataRow label="State" value={shipment.consignee_state} />
            <DataRow label="ZIP Code" value={shipment.consignee_zip} />
            <DataRow label="Country" value={shipment.consignee_country} />
            <DataRow label="Contact Person" value={shipment.consignee_contact} />
            <DataRow label="Phone" value={shipment.consignee_phone} />
            <DataRow label="Email" value={shipment.consignee_email} />
            <DataRow label="Reference Number" value={shipment.consignee_reference_num} />
          </div>

          {/* Pickup & Delivery */}
          <SectionHeader title="PICKUP & DELIVERY" />
          <div className="detail-section">
            <DataRow label="Pickup Date" value={formatDate(shipment.pickup_date)} highlight />
            {shipment.pickup_window_start && (
              <DataRow 
                label="Pickup Window" 
                value={`${shipment.pickup_window_start} - ${shipment.pickup_window_end}`} 
              />
            )}
            <DataRow label="Delivery Date" value={formatDate(shipment.delivery_date)} highlight />
            {shipment.delivery_window_start && (
              <DataRow 
                label="Delivery Window" 
                value={`${shipment.delivery_window_start} - ${shipment.delivery_window_end}`} 
              />
            )}
          </div>

          {/* Accessorials */}
          {(!!shipment.acc_liftgate_air || !!shipment.acc_liftgate_gr ||
            !!shipment.acc_inside_air || !!shipment.acc_inside_gr ||
            !!shipment.acc_rigging_air || !!shipment.acc_rigging_gr ||
            !!shipment.acc_construction_air || !!shipment.acc_construction_gr ||
            !!shipment.acc_tarp_needed || !!shipment.acc_protect_freezing) && (
            <>
              <SectionHeader title="ACCESSORIALS" />
              <div className="detail-section">
                <div className="accessorials-list">
                  {!!shipment.acc_liftgate_air && <span className="accessorial-badge">Liftgate (Air)</span>}
                  {!!shipment.acc_liftgate_gr && <span className="accessorial-badge">Liftgate (Ground)</span>}
                  {!!shipment.acc_inside_air && <span className="accessorial-badge">Inside Delivery (Air)</span>}
                  {!!shipment.acc_inside_gr && <span className="accessorial-badge">Inside Delivery (Ground)</span>}
                  {!!shipment.acc_rigging_air && <span className="accessorial-badge">Rigging (Air)</span>}
                  {!!shipment.acc_rigging_gr && <span className="accessorial-badge">Rigging (Ground)</span>}
                  {!!shipment.acc_construction_air && <span className="accessorial-badge">Construction (Air)</span>}
                  {!!shipment.acc_construction_gr && <span className="accessorial-badge">Construction (Ground)</span>}
                  {!!shipment.acc_tarp_needed && <span className="accessorial-badge">Tarp Needed</span>}
                  {!!shipment.acc_protect_freezing && <span className="accessorial-badge">Protect from Freezing</span>}
                </div>
              </div>
            </>
          )}

          {/* Line Items */}
          {shipment.line_items && shipment.line_items.length > 0 && (
            <>
              <SectionHeader title="LINE ITEMS" />
              <div className="detail-section">
                <div className="line-items-table-container">
                  <table className="line-items-table">
                    <thead>
                      <tr>
                        <th>Pcs</th>
                        <th>Type</th>
                        <th>Weight</th>
                        <th>Dimensions (L×W×H)</th>
                        <th>Description</th>
                        <th>Class</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.line_items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.pieces || item.handling_units || '0'}</td>
                          <td>{item.type}</td>
                          <td><strong>{formatLineItemWeight(item)}</strong></td>
                          <td>{formatLineItemDimensions(item)}</td>
                          <td>{item.description}</td>
                          <td>{item.freight_class || item.class}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Attachments */}
          <AttachmentSection
            shipmentId={shipment.id}
            currentUser={currentUser}
            shipmentOwnerId={shipment.user_id}
          />

          {/* Additional Information */}
          {(shipment.product_location || shipment.special_instructions) && (
            <>
              <SectionHeader title="ADDITIONAL INFORMATION" />
              <div className="detail-section">
                <DataRow label="Product Location" value={shipment.product_location} />
                {shipment.special_instructions && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Special Instructions:</span>
                    <div className="detail-value special-instructions">
                      {shipment.special_instructions}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* International Fields */}
          {shipment.shipment_type === 'international' && (
            <>
              <SectionHeader title="INTERNATIONAL DETAILS" />
              <div className="detail-section">
                <DataRow label="Incoterm" value={shipment.incoterm} />
                <DataRow label="Incoterm Location" value={shipment.incoterm_location} />
                <DataRow label="Brokerage Name" value={shipment.brokerage_name} />
                <DataRow label="Brokerage Number" value={shipment.brokerage_number} />
                <DataRow label="Import / Export" value={shipment.import_export} highlight />
                <DataRow label="Finished Turbine Part" value={flagYesNo(shipment.finished_turbine_part)} />
                <DataRow label="HTS Code" value={shipment.hts_code_selection} highlight />
                <DataRow label="HTS Description" value={shipment.hts_description} />
                <DataRow label="Country of Origin" value={shipment.country_of_origin} highlight />
                <DataRow label="Original Value / Piece" value={shipment.original_value_per_piece != null ? `$${parseFloat(shipment.original_value_per_piece).toFixed(2)}` : null} />
                <DataRow label="Added Value / Piece" value={shipment.added_value_per_piece != null ? `$${parseFloat(shipment.added_value_per_piece).toFixed(2)}` : null} />
                {flagYesNo(shipment.tib_required) && (
                  <DataRow
                    label="TIB (Temporary Import Bond)"
                    value={flagYesNo(shipment.tib_required)}
                    highlight={isYesFlag(shipment.tib_required)}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {canEditResubmit && (
            <button
              className="btn-primary"
              onClick={() => {
                const formPath = shipment.shipment_type === 'international'
                  ? '/new-intl-shipment'
                  : '/new-shipment';
                navigate(formPath, { state: { editData: shipment, editMode: true } });
                onClose();
              }}
            >
              Edit &amp; Resubmit
            </button>
          )}
          {showCloneButton && (
            <button className="btn-primary" onClick={() => { onClone(shipment); onClose(); }}>
              Clone Shipment
            </button>
          )}
          {canReject && (
            <button className="btn-danger" onClick={() => setShowRejectionModal(true)}>
              Reject Shipment
            </button>
          )}
          {canApprove && (
            <button
              className="btn-primary"
              style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
              onClick={async () => {
                try {
                  await shipmentsAPI.approve(shipment.id);
                  if (onReject) onReject();
                  onClose();
                } catch (err) {
                  console.error('Failed to approve:', err);
                }
              }}
            >
              Approve Shipment
            </button>
          )}
          {showDeleteButton && (
            <button className="btn-danger" onClick={() => { onDelete(); onClose(); }}>
              Delete Shipment
            </button>
          )}
        </div>

        {/* Rejection Modal */}
        {showRejectionModal && (
          <RejectionModal
            shipmentId={shipment.id}
            onConfirm={async (reason) => {
              await shipmentsAPI.reject(shipment.id, reason);
              setShowRejectionModal(false);
              if (onReject) onReject();
              onClose();
            }}
            onCancel={() => setShowRejectionModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default ShipmentDetailModal;
